import { API_ROOT } from '../config/apiConfig';
import {
  authFetchInit,
  getAuthSessionUser,
  getAuthToken,
  isHttpOnlyAuthSessionEnabled,
} from './authSession';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const SKIP_PATH_FRAGMENTS = ['/audit/events', '/auth/validate', '/health'];

const SENSITIVE_KEYS = new Set([
  'password',
  'current_password',
  'new_password',
  'client_secret',
  'secret',
  'token',
  'access_token',
  'refresh_token',
]);

function sanitize(value: unknown, depth = 0): unknown {
  if (depth > 3 || value == null) return value;
  if (Array.isArray(value)) return value.slice(0, 30).map((v) => sanitize(v, depth + 1));
  if (typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? '[REDACTED]' : sanitize(v, depth + 1);
  }
  return out;
}

function resolveEntityId(candidate?: string): string {
  if (candidate && UUID_RE.test(candidate)) return candidate;
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return '00000000-0000-4000-8000-000000000001';
}

function inferFromUrl(url: string): { entity_type: string; entity_name: string; entity_id?: string } {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const parts = path.split('/').filter(Boolean);
    const v1 = parts.indexOf('v1');
    const segs = v1 >= 0 ? parts.slice(v1 + 1) : parts;
    const resource = segs[0] || 'system';
    const maybeId = segs[1];
    const id =
      maybeId && !['import', 'template', 'events', 'stats', 'login', 'exchange', 'users'].includes(maybeId)
        ? maybeId
        : undefined;
    const entity_type = resource.replace(/-/g, '_');
    return { entity_type, entity_name: id ? `${entity_type} ${id}` : resource, entity_id: id };
  } catch {
    return { entity_type: 'system', entity_name: 'system' };
  }
}

function methodToEvent(method: string): string {
  switch (method.toUpperCase()) {
    case 'POST':
      return 'create';
    case 'PUT':
    case 'PATCH':
      return 'update';
    case 'DELETE':
      return 'delete';
    default:
      return 'update';
  }
}

function buildDescription(method: string, url: string, status: number): string {
  const { entity_type, entity_name } = inferFromUrl(url);
  const evt = methodToEvent(method);
  return `${evt.charAt(0).toUpperCase() + evt.slice(1)} ${entity_type.replace(/_/g, ' ')} — ${entity_name} (${status})`;
}

function shouldSkip(url: string): boolean {
  const lower = url.toLowerCase();
  return SKIP_PATH_FRAGMENTS.some((f) => lower.includes(f));
}

function readUser(): { id: string; username: string; role: string; enterprise_id?: string } | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw) as Record<string, string>;
    if (!u.id) return null;
    return {
      id: u.id,
      username: u.username || u.email || 'user',
      role: u.role || 'user',
      enterprise_id: u.enterprise_id,
    };
  } catch {
    return null;
  }
}

/** Fire-and-forget audit log for successful mutating API calls from the UI. */
export async function logApiActivityAudit(
  method: string,
  url: string,
  status: number,
  requestData?: unknown,
  responseData?: unknown,
  responseHeaders?: Record<string, unknown>
): Promise<void> {
  const alreadyRecorded =
    responseHeaders &&
    (responseHeaders['x-audit-recorded'] === '1' ||
      responseHeaders['X-Audit-Recorded'] === '1');
  if (alreadyRecorded) return;
  const m = method.toUpperCase();
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(m)) return;
  if (status < 200 || status >= 400) return;
  if (shouldSkip(url)) return;

  const token = getAuthToken();
  const user = readUser() || getAuthSessionUser();
  if (isHttpOnlyAuthSessionEnabled()) {
    if (!user) return;
  } else if (!token || !user) {
    return;
  }

  const { entity_type, entity_name, entity_id } = inferFromUrl(url);
  const entityId = resolveEntityId(entity_id);

  let event_type = methodToEvent(m);
  if (url.includes('/approve')) event_type = 'approve';
  if (url.includes('/reject')) event_type = 'reject';
  if (url.includes('/delegate')) event_type = 'delegate';
  if (url.includes('/import')) event_type = 'submit';

  const payload = {
    event_type,
    entity_type,
    entity_id: entityId,
    entity_name,
    user_id: user.id,
    user_name: user.username,
    user_role: user.role,
    action_description: buildDescription(m, url, status),
    details: {
      audit_source: 'client-interceptor',
      method: m,
      url,
      status_code: status,
      ...(entity_id && entity_id !== entityId ? { source_entity_id: entity_id } : {}),
      ...(requestData ? { request: sanitize(requestData) } : {}),
      ...(responseData ? { response: sanitize(responseData) } : {}),
    },
    criticality_level: url.includes('/auth') || url.includes('/security') ? 'high' : 'medium',
  };

  try {
    await fetch(
      `${API_ROOT}/audit/events`,
      authFetchInit({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    );
  } catch {
    /* non-blocking */
  }
}
